import React, { useState } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import * as XLSX from "xlsx";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Upload, FileSpreadsheet,Save,Trash2,FileCheck,Eye,AlertCircle,RefreshCw,X,CheckCircle, FileText,
} from "lucide-react";

const Charges = () => {
  const [excelFileName, setExcelFileName] = useState("");
  const [excelSheetName, setExcelSheetName] = useState("");
  const [excelRows, setExcelRows] = useState([]);
  const [excelCols, setExcelCols] = useState([]);
  const [status, setStatus] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const societyName = localStorage.getItem("society");

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const sanitizeFileName = (name = "") => {
    return name
      .replace(/\.[^/.]+$/, "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .slice(0, 64);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);
    setStatus("Parsing file...");
    setExcelFileName(file.name);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const firstSheet = workbook.SheetNames[0];
      setExcelSheetName(firstSheet);

      const worksheet = workbook.Sheets[firstSheet];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const cols = jsonData.length ? Object.keys(jsonData[0]) : [];
      setExcelCols(cols);
      setExcelRows(jsonData);
      setStatus(`${jsonData.length} rows loaded from "${firstSheet}"`);
      toast.success("File parsed successfully!");
    } catch (err) {
      console.error(err);
      setStatus("Failed to parse Excel file.");
      toast.error("Failed to parse Excel file.");
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = async () => {
    if (!excelRows.length) {
      return toast.warning(
        "No Excel data to upload. Please choose a file first.",
      );
    }

    setUploading(true);
    setStatus("Uploading...");

    try {
      const res = await axios.post(
        `${BASE_URL}/charges/add-charges`,
        {
          fileName: sanitizeFileName(
            excelFileName || excelSheetName || "excel_upload",
          ),
          sheetName: excelSheetName,
          rows: excelRows,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      setStatus(res?.data?.message || "Upload successful");
      toast.success("Charges uploaded successfully!");
    } catch (err) {
      console.error(err);
      setStatus("Upload failed.");
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileUploadFormData = async () => {
    const fileInput = document.getElementById("excel-file-input");
    const file = fileInput?.files?.[0];
    if (!file) return toast.warning("Select a file first.");

    setUploading(true);
    const sanitizedTable = sanitizeFileName(file.name || "excel_upload");
    const form = new FormData();
    form.append("file", file);
    form.append("tableName", sanitizedTable);
    form.append("societyName", societyName);

    try {
      const res = await axios.post(`${BASE_URL}/charges/add-charges`, form);
      toast.success("File uploaded successfully!");
      setStatus(`File uploaded: ${file.name}`);
    } catch (err) {
      console.error("Upload failed:", err?.response?.data || err.message);
      toast.error(
        "Upload failed: " + (err?.response?.data?.message || err.message),
      );
      setStatus("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setExcelRows([]);
    setExcelCols([]);
    setExcelFileName("");
    setStatus("");
    const fileInput = document.getElementById("excel-file-input");
    if (fileInput) fileInput.value = "";
  };

  return (
    <div className="app-container">
      <style>
        {`
        /* ========== CHARGES PAGE STYLING ========== */
        .app-container {
          height: 100vh;
          overflow: hidden !important;
        }

        .main-layout {
          height: calc(100vh - 64px);
          overflow: hidden !important;
        }

        .content-area {
          height: 100%;
          overflow-y: auto !important;
          overflow-x: hidden;
          padding-bottom: 20px;
          background: #f8fafc;
        }

        /* ========== CHARGES CONTAINER ========== */
        .charges-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
          min-height: calc(100vh - 160px);
        }

        .charges-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
          margin-bottom: 20px;
        }

        /* Header matching Receipt.js style */
        .charges-header {
          
          color: white;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .charges-header h2 {
          font-size: 20px;
          font-weight: 700;
          margin: 0;
          color: white;
        }

        /* Charges Body */
        .charges-body {
          padding: 30px;
        }

        /* Upload Area */
        .upload-area {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          padding: 40px 20px;
          text-align: center;
          background: #f8fafc;
          transition: all 0.3s ease;
          cursor: pointer;
          position: relative;
          margin-bottom: 24px;
        }

        .upload-area:hover {
          border-color: #0d6efd;
          background: #f0f9ff;
        }

        .upload-area.has-file {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .upload-input {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          opacity: 0;
          cursor: pointer;
          z-index: 2;
        }

        .upload-content {
          pointer-events: none;
          z-index: 1;
        }

        .upload-icon-wrapper {
          margin-bottom: 16px;
        }

        .upload-text h3 {
          font-size: 18px;
          color: #1e293b;
          margin: 0 0 8px 0;
          font-weight: 600;
        }

        .upload-text p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .file-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
        }

        .file-status {
          font-size: 14px;
          color: #10b981;
          font-weight: 500;
        }

        /* Status Display */
        .status-display {
          padding: 12px 16px;
          background: #f8fafc;
          border-radius: 8px;
          margin-bottom: 24px;
          border-left: 4px solid #0d6efd;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .status-display.success {
          border-left-color: #10b981;
          background: #f0fdf4;
        }

        .status-display.error {
          border-left-color: #dc2626;
          background: #fef2f2;
        }

        .status-display.info {
          border-left-color: #0d6efd;
          background: #f0f9ff;
        }

        .status-text {
          font-size: 14px;
          color: #1e293b;
          font-weight: 500;
          flex: 1;
        }

        /* Action Buttons */
        .charges-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 24px;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          min-width: 160px;
          height: 44px;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .upload-btn {
          background: #0d6efd;
          color: white;
        }

        .upload-btn:hover:not(:disabled) {
          background: #0b5ed7;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
        }

        .upload-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .clear-btn {
          background: #dc2626;
          color: white;
        }

        .clear-btn:hover:not(:disabled) {
          background: #b91c1c;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }

        .clear-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .action-btn span {
          white-space: nowrap;
        }

        /* Preview Section */
        .preview-section {
          margin-top: 20px;
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .preview-header {
          background: #f8fafc;
          padding: 16px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .preview-header h3 {
          font-size: 16px;
          color: #374151;
          margin: 0;
          font-weight: 600;
        }

        .preview-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 24px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .preview-info span {
          font-size: 13px;
          color: #64748b;
        }

        .preview-info strong {
          color: #1e293b;
        }

        /* Table Styling */
        .table-container {
          max-height: 500px;
          overflow: auto;
          border-radius: 0 0 8px 8px;
        }

        .charges-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }

        .charges-table thead {
          background: #f8fafc;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .charges-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #475569;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .charges-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 13px;
          color: #1e293b;
          vertical-align: middle;
        }

        .charges-table tbody tr:hover {
          background-color: #f8fafc;
        }

        .charges-table tbody tr:last-child td {
          border-bottom: none;
        }

        /* Loading States */
        .spin-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Empty State */
        .empty-state {
          padding: 60px 20px;
          text-align: center;
          background: #f8fafc;
          border-radius: 8px;
        }

        .empty-state svg {
          color: #94a3b8;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .empty-state p {
          color: #64748b;
          font-size: 13px;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .charges-container {
            padding: 12px;
          }

          .charges-body {
            padding: 20px;
          }

          .charges-actions {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
            min-width: unset;
          }

          .upload-area {
            padding: 30px 15px;
          }

          .charges-table th,
          .charges-table td {
            padding: 8px 12px;
            font-size: 12px;
          }
        }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />
          <ToastContainer position="bottom-right" autoClose={3000} />

          <div className="charges-container">
            <div className="charges-card">
              <div className="charges-header bg-gradient-to-r from-indigo-500 to-blue-500">
                <FileSpreadsheet size={24} />
                <h2>Upload Charges Excel</h2>
              </div>

              <div className="charges-body">
                <div
                  className={`upload-area ${excelFileName ? "has-file" : ""}`}
                >
                  <input
                    id="excel-file-input"
                    type="file"
                    accept=".xls,.xlsx,.csv"
                    onChange={handleFile}
                    className="upload-input"
                  />
                  <div className="upload-content">
                    <div className="upload-icon-wrapper">
                      {parsing ? (
                        <RefreshCw
                          size={32}
                          className="spin-animation"
                          color="#0d6efd"
                        />
                      ) : excelFileName ? (
                        <FileCheck size={32} color="#10b981" />
                      ) : (
                        <Upload size={32} color="#64748b" />
                      )}
                    </div>
                    <div className="upload-text">
                      {excelFileName ? (
                        <div className="file-info">
                          <span className="file-name">{excelFileName}</span>
                          <span className="file-status">
                            {parsing ? "Parsing..." : status || "File ready"}
                          </span>
                        </div>
                      ) : (
                        <>
                          <h3>Click to upload or drag and drop</h3>
                          <p>Supported formats: .xls, .xlsx, .csv</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {status && (
                  <div
                    className={`status-display ${status.includes("successful") ? "success" : status.includes("failed") ? "error" : "info"}`}
                  >
                    {status.includes("successful") ? (
                      <CheckCircle size={18} color="#10b981" />
                    ) : status.includes("failed") ? (
                      <AlertCircle size={18} color="#dc2626" />
                    ) : (
                      <FileText size={18} color="#0d6efd" />
                    )}
                    <span className="status-text">{status}</span>
                  </div>
                )}
                <div className="charges-actions">
                  <button
                    className="action-btn upload-btn"
                    onClick={handleUpload}
                    disabled={!excelRows.length || uploading}
                  >
                    {uploading ? (
                      <RefreshCw size={18} className="spin-animation" />
                    ) : (
                      <Save size={18} />
                    )}
                    <span>
                      {uploading ? "Uploading..." : "Upload Excel (Save)"}
                    </span>
                  </button>

                  <button
                    className="action-btn upload-btn"
                    onClick={handleFileUploadFormData}
                    disabled={!excelFileName || uploading}
                  >
                    {uploading ? (
                      <RefreshCw size={18} className="spin-animation" />
                    ) : (
                      <Upload size={18} />
                    )}
                    <span>
                      {uploading ? "Uploading..." : "Upload File (FormData)"}
                    </span>
                  </button>

                  <button
                    className="action-btn clear-btn"
                    onClick={handleClear}
                    disabled={!excelFileName || uploading}
                  >
                    <Trash2 size={18} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
            </div>

            {excelRows.length > 0 && (
              <div className="charges-card preview-section">
                <div className="preview-header">
                  <Eye size={20} color="#374151" />
                  <h3>Data Preview</h3>
                </div>

                <div className="preview-info">
                  <span>
                    <strong>File:</strong> {excelFileName}
                  </span>
                  <span>
                    <strong>Sheet:</strong> {excelSheetName}
                  </span>
                  <span>
                    <strong>Rows:</strong> {excelRows.length}
                  </span>
                  <span>
                    <strong>Columns:</strong> {excelCols.length}
                  </span>
                </div>

                <div className="table-container">
                  <table className="charges-table">
                    <thead>
                      <tr>
                        {excelCols.map((c) => (
                          <th key={c}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelRows.slice(0, 100).map((row, ri) => (
                        <tr key={ri}>
                          {excelCols.map((col, ci) => (
                            <td key={ri + "_" + ci}>
                              {String(row[col] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {excelRows.length > 100 && (
                        <tr>
                          <td
                            colSpan={excelCols.length}
                            style={{
                              textAlign: "center",
                              color: "#64748b",
                              fontStyle: "italic",
                            }}
                          >
                            ... and {excelRows.length - 100} more rows
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Charges;
